import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    alertLevel, AlertSettings, elapsedText, itemsByCourse, KitchenTicket, loadBoard, loadSettings, minutesSince, NEXT_LABEL,
    NEXT_STATUS, newTicketIds, PREVIOUS_STATUS, saveKitchenDisplay, saveSettings, setTicketStatus, TicketStatus,
} from './kitchenDisplay';

// Cada cuánto se consultan las comandas y se actualizan los tiempos
const POLL_MS = 10000;
const TICK_MS = 15000;

const COLUMNS: { status: TicketStatus; title: string; icon: string }[] = [
    { status: 'PEN', title: 'Pendientes', icon: 'bi-inbox' },
    { status: 'PRE', title: 'En preparación', icon: 'bi-fire' },
    { status: 'LIS', title: 'Listas para servir', icon: 'bi-bell' },
];

const ALERT_STYLES = {
    ok: { border: '#dee2e6', header: '#f8f9fa', text: '#6c757d' },
    warn: { border: '#ffc107', header: '#fff3cd', text: '#997404' },
    late: { border: '#dc3545', header: '#f8d7da', text: '#b02a37' },
};

// Aviso corto cuando llega una comanda nueva
const beep = () => {
    try {
        const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
        const ctx = new AudioCtx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = 880;
        gain.gain.value = 0.15;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.25);
    } catch { /* sin audio */ }
};

export const KitchenPage = () => {
    const [tickets, setTickets] = useState<KitchenTicket[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState<number | null>(null);
    const [settings, setSettings] = useState<AlertSettings>(loadSettings);
    const [showSettings, setShowSettings] = useState(false);
    const [showDelivered, setShowDelivered] = useState(false);
    const [offset, setOffset] = useState(0);            // diferencia con la hora del servidor
    const [now, setNow] = useState(Date.now());
    // Ajuste del comercio: con pantalla (por defecto) o imprimiendo la comanda en los equipos
    const [kitchenDisplay, setKitchenDisplay] = useState(true);
    const [savingDisplay, setSavingDisplay] = useState(false);

    const ticketsRef = useRef<KitchenTicket[]>([]);
    const savingDisplayRef = useRef(false);
    const settingsRef = useRef(settings);
    settingsRef.current = settings;

    const refresh = useCallback(async (initial = false) => {
        try {
            const board = await loadBoard();
            const fresh = newTicketIds(ticketsRef.current, board.tickets);
            if (!initial && fresh.length > 0 && settingsRef.current.sound) beep();
            ticketsRef.current = board.tickets;
            setTickets(board.tickets);
            setOffset(new Date(board.serverTime).getTime() - Date.now());
            if (!savingDisplayRef.current) setKitchenDisplay(board.kitchenDisplay !== false);
            setError('');
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh(true);
        const poll = setInterval(() => refresh(), POLL_MS);
        const tick = setInterval(() => setNow(Date.now()), TICK_MS);
        return () => { clearInterval(poll); clearInterval(tick); };
    }, [refresh]);

    const serverNow = now + offset;

    const changeStatus = async (ticket: KitchenTicket, status: TicketStatus) => {
        setBusyId(ticket.id);
        try {
            await setTicketStatus(ticket.id, status);
            // Se refleja al instante; la próxima consulta trae las fechas del servidor
            const updated = ticketsRef.current.map(t => t.id === ticket.id ? { ...t, status } : t);
            ticketsRef.current = updated;
            setTickets(updated);
            refresh();
        } catch (e: any) {
            setError(e.message);
        } finally {
            setBusyId(null);
        }
    };

    const toggleKitchenDisplay = async (value: boolean) => {
        savingDisplayRef.current = true;
        setSavingDisplay(true);
        setKitchenDisplay(value);
        try {
            await saveKitchenDisplay(value);
            setError('');
        } catch (e: any) {
            setKitchenDisplay(!value);
            setError(e.message);
        } finally {
            savingDisplayRef.current = false;
            setSavingDisplay(false);
        }
    };

    const updateSettings = (changes: Partial<AlertSettings>) => {
        const next = { ...settings, ...changes };
        setSettings(next);
        saveSettings(next);
    };

    const toggleFullscreen = () => {
        if (document.fullscreenElement) document.exitFullscreen?.();
        else document.documentElement.requestFullscreen?.();
    };

    const byStatus = useMemo(() => {
        const map: Record<TicketStatus, KitchenTicket[]> = { PEN: [], PRE: [], LIS: [], ENT: [] };
        tickets.forEach(t => map[t.status]?.push(t));
        // Las anulaciones primero: la cocina debe verlas antes de seguir
        map.PEN.sort((a, b) => (a.type === 'ANU' ? -1 : 0) - (b.type === 'ANU' ? -1 : 0));
        return map;
    }, [tickets]);

    const lateCount = tickets.filter(t => alertLevel(t, serverNow, settings) === 'late').length;

    // =============================
    // Comanda
    // =============================
    const ticketCard = (t: KitchenTicket) => {
        const level = alertLevel(t, serverNow, settings);
        const style = t.type === 'ANU' ? ALERT_STYLES.late : ALERT_STYLES[level];
        const minutes = minutesSince(t.firedAt, serverNow);
        const next = t.type === 'ANU' ? (t.status === 'PEN' ? 'ENT' : null) : NEXT_STATUS[t.status];
        const previous = t.type === 'ANU' ? null : PREVIOUS_STATUS[t.status];
        const busy = busyId === t.id;

        return (
            <div key={t.id} className="card shadow-sm mb-3" style={{ borderWidth: 2, borderColor: style.border }}>
                <div className="card-header d-flex justify-content-between align-items-start py-2" style={{ backgroundColor: style.header }}>
                    <div>
                        <div className="fw-bold fs-5 lh-sm">
                            {t.takeout ? 'Para llevar' : t.table ?? 'Sin mesa'}
                            <span className="text-muted fw-normal fs-6 ms-2">#{t.orderNumber ?? t.orderId}</span>
                        </div>
                        <div className="small text-muted">
                            {t.type === 'ANU'
                                ? <span className="fw-semibold text-danger"><i className="bi bi-x-octagon me-1" />ANULACIÓN</span>
                                : t.courses ?? ''}
                            {t.waiter ? ` · ${t.waiter}` : ''}
                        </div>
                    </div>
                    <div className="text-end fw-bold" style={{ color: style.text }} title="Tiempo desde que se envió a cocina">
                        {level !== 'ok' && <i className="bi bi-alarm me-1" />}
                        {elapsedText(minutes)}
                    </div>
                </div>
                <div className="card-body py-2">
                    {itemsByCourse(t.items).map(group => (
                        <div key={group.course} className="mb-1">
                            {t.type === 'MAR' && t.items.some(i => i.course !== group.course) &&
                                <div className="small text-uppercase text-muted fw-semibold">{group.name}</div>}
                            {group.items.map((item, i) => (
                                <div key={i} className="py-1 border-bottom">
                                    <div className={`fs-6 ${t.type === 'ANU' ? 'text-decoration-line-through text-danger' : ''}`}>
                                        <span className="fw-bold me-2">{Number(item.quantity).toLocaleString()}×</span>{item.name}
                                    </div>
                                    {item.note && <div className={`small ${t.type === 'ANU' ? 'text-muted' : 'text-primary-emphasis'}`}>
                                        {t.type === 'ANU' ? 'Motivo: ' : ''}{item.note}
                                    </div>}
                                </div>
                            ))}
                        </div>
                    ))}
                    {t.orderNote && t.type === 'MAR' && <div className="small text-muted mt-1"><i className="bi bi-chat-left-text me-1" />{t.orderNote}</div>}
                </div>
                <div className="card-footer bg-white d-flex gap-2 py-2">
                    {previous && (
                        <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} title="Devolver al estado anterior"
                            onClick={() => changeStatus(t, previous)}>
                            <i className="bi bi-arrow-counterclockwise" />
                        </button>
                    )}
                    {next && (
                        <button type="button" className={`btn btn-sm flex-grow-1 ${t.type === 'ANU' ? 'btn-danger' : next === 'LIS' ? 'btn-success' : 'btn-primary'}`}
                            disabled={busy} onClick={() => changeStatus(t, next)}>
                            {busy ? <span className="spinner-border spinner-border-sm" /> : t.type === 'ANU' ? 'Enterado' : NEXT_LABEL[t.status]}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // =============================
    // Render
    // =============================
    return (
        <div className="mt-4 container-fluid px-3" style={{ flex: 1 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                    <h3 className="mb-0">Pantalla de cocina</h3>
                    <div className="text-muted small">
                        {byStatus.PEN.length} pendientes · {byStatus.PRE.length} en preparación · {byStatus.LIS.length} listas
                        {lateCount > 0 && <span className="text-danger fw-semibold ms-2"><i className="bi bi-alarm me-1" />{lateCount} atrasadas</span>}
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary" title={settings.sound ? 'Silenciar aviso' : 'Activar aviso'}
                        onClick={() => updateSettings({ sound: !settings.sound })}>
                        <i className={`bi ${settings.sound ? 'bi-volume-up' : 'bi-volume-mute'}`} />
                    </button>
                    <button type="button" className="btn btn-outline-secondary" title="Ajustes" onClick={() => setShowSettings(s => !s)}>
                        <i className="bi bi-sliders" />
                    </button>
                    <button type="button" className="btn btn-outline-secondary" title="Pantalla completa" onClick={toggleFullscreen}>
                        <i className="bi bi-arrows-fullscreen" />
                    </button>
                </div>
            </div>

            {showSettings && (
                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body border-bottom">
                        <div className="form-check form-switch mb-1">
                            <input className="form-check-input" type="checkbox" role="switch" id="kitchen-display"
                                checked={kitchenDisplay} disabled={savingDisplay}
                                onChange={e => toggleKitchenDisplay(e.target.checked)} />
                            <label className="form-check-label fw-semibold" htmlFor="kitchen-display">Usar pantalla de cocina</label>
                        </div>
                        <div className="small text-muted">
                            Encendida, al marchar la comanda llega a esta pantalla y no se imprime. Apagada, los equipos
                            ofrecen imprimir la comanda. Aplica a todos los equipos del comercio.
                        </div>
                    </div>
                    <div className="card-body d-flex flex-wrap gap-3 align-items-end">
                        <div>
                            <label className="form-label small fw-semibold mb-1">Avisar a los (minutos)</label>
                            <input type="number" min={1} className="form-control" style={{ width: 120 }} value={settings.warn}
                                onChange={e => updateSettings({ warn: Math.max(1, parseInt(e.target.value || '1')) })} />
                        </div>
                        <div>
                            <label className="form-label small fw-semibold mb-1">Atrasada a los (minutos)</label>
                            <input type="number" min={1} className="form-control" style={{ width: 120 }} value={settings.late}
                                onChange={e => updateSettings({ late: Math.max(1, parseInt(e.target.value || '1')) })} />
                        </div>
                        <div className="small text-muted">Se cuenta desde que el mesero envía la comanda. Se guarda en este equipo.</div>
                    </div>
                </div>
            )}

            {!kitchenDisplay && (
                <div className="alert alert-warning d-flex align-items-start gap-2 py-2">
                    <i className="bi bi-printer mt-1" />
                    <div className="small">
                        La pantalla de cocina está apagada: al marchar, los equipos ofrecen imprimir la comanda.
                        Puede encenderla en <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setShowSettings(true)}>Ajustes</button>.
                    </div>
                </div>
            )}

            {error && (
                <div className="alert alert-danger d-flex align-items-start gap-2 py-2">
                    <i className="bi bi-exclamation-circle mt-1" />
                    <div className="small">{error}</div>
                </div>
            )}

            {loading ? (
                <div className="text-center text-muted py-5"><span className="spinner-border spinner-border-sm me-2" />Cargando comandas...</div>
            ) : (
                <div className="row g-3">
                    {COLUMNS.map(col => (
                        <div key={col.status} className="col-12 col-lg-4">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <i className={`bi ${col.icon}`} />
                                <h5 className="mb-0">{col.title}</h5>
                                <span className="badge bg-secondary">{byStatus[col.status].length}</span>
                            </div>
                            {byStatus[col.status].length === 0
                                ? <div className="text-muted small border rounded p-3 text-center">Sin comandas</div>
                                : byStatus[col.status].map(ticketCard)}
                        </div>
                    ))}
                </div>
            )}

            {byStatus.ENT.length > 0 && (
                <div className="mt-2 mb-4">
                    <button type="button" className="btn btn-link px-0" onClick={() => setShowDelivered(s => !s)}>
                        <i className={`bi ${showDelivered ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`} />
                        Entregadas hace poco ({byStatus.ENT.length})
                    </button>
                    {showDelivered && (
                        <div className="row g-3 mt-1">
                            {byStatus.ENT.map(t => <div key={t.id} className="col-12 col-md-6 col-xl-3">{ticketCard(t)}</div>)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
